// src/contexts/ThemeDownloadContext.js - 환경 판단 로직 수정 완료

import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { debugLog, getEnvironment } from '../utils/environment'; // [수정] getEnvironment 추가
import imageLoader from '../services/imageLoader';

const ThemeDownloadContext = createContext();

export const useThemeDownloadContext = () => {
  const context = useContext(ThemeDownloadContext);
  if (!context) {
    throw new Error('useThemeDownloadContext must be used within ThemeDownloadProvider');
  }
  return context;
};

export const ThemeDownloadProvider = ({ children }) => {
  // [2025-01-11 수정] getEnvironment() 함수 사용으로 환경 판단 통일
  const [environment] = useState(() => {
    const env = getEnvironment(); // window.__APP_ENVIRONMENT__ 대신 getEnvironment() 사용
    debugLog('[ThemeDownloadContext] 환경 설정:', env);
    return env;
  });
  
  // 환경별 키 사용
  const DOWNLOAD_STATUS_KEY = `painori-download-status-${environment}`;
  
  // 상태 검증 함수
  const validateDownloadStatus = (savedStatus) => {
    if (!savedStatus || typeof savedStatus !== 'object') return savedStatus;
    
    const validatedStatus = {};
    const now = Date.now();
    const FIVE_MINUTES = 5 * 60 * 1000;
    
    Object.entries(savedStatus).forEach(([themeId, statusData]) => {
      // 하위 호환성: 기존 문자열 형식 처리
      if (typeof statusData === 'string') {
        if (statusData === 'downloading') {
          // 기존 'downloading' 상태는 검증 필요
          if (imageLoader.isActuallyDownloading && imageLoader.isActuallyDownloading(themeId)) {
            // 실제로 다운로드 중이면 유지
            validatedStatus[themeId] = {
              status: 'downloading',
              timestamp: now
            };
          } else {
            // 실제로 다운로드 중이 아니면 리셋
            debugLog(`[ThemeDownloadContext] 검증 실패 - ${themeId} 상태를 'not-downloaded'로 리셋`);
            validatedStatus[themeId] = 'not-downloaded';
          }
        } else {
          // downloading이 아닌 상태는 그대로 유지
          validatedStatus[themeId] = statusData;
        }
      } 
      // 새로운 객체 형식 처리
      else if (statusData && typeof statusData === 'object') {
        const { status, timestamp } = statusData;
        
        if (status === 'downloading') {
          // timestamp 확인
          const isExpired = timestamp && (now - timestamp > FIVE_MINUTES);
          // 실제 다운로드 상태 확인
          const isActuallyDownloading = imageLoader.isActuallyDownloading && 
                                        imageLoader.isActuallyDownloading(themeId);
          
          if (isExpired || !isActuallyDownloading) {
            debugLog(`[ThemeDownloadContext] 다운로드 상태 만료/무효 - ${themeId} 리셋`);
            validatedStatus[themeId] = 'not-downloaded';
          } else {
            validatedStatus[themeId] = statusData;
          }
        } else {
          // downloading이 아닌 상태는 그대로 유지
          validatedStatus[themeId] = statusData;
        }
      }
    });
    
    return validatedStatus;
  };
  
  // 다운로드 상태 관리 - 무료 테마도 초기값은 not-downloaded
  const [downloadStatus, setDownloadStatus] = useState(() => {
    const saved = localStorage.getItem(DOWNLOAD_STATUS_KEY);
    if (saved) {
      try {
        const parsedStatus = JSON.parse(saved);
        // 저장된 상태 검증 후 사용
        const validatedStatus = validateDownloadStatus(parsedStatus);
        debugLog('[ThemeDownloadContext] 검증된 상태:', validatedStatus);
        return validatedStatus;
      } catch (error) {
        debugLog('[ThemeDownloadContext] localStorage 파싱 실패:', error);
        return {};
      }
    }
    return {};
  });
  
  const [downloadProgress, setDownloadProgress] = useState({});
  
  // 다운로드 상태 저장
  useEffect(() => {
    localStorage.setItem(DOWNLOAD_STATUS_KEY, JSON.stringify(downloadStatus));
  }, [downloadStatus, DOWNLOAD_STATUS_KEY]);
  
  // 다운로드 상태 업데이트 함수 - timestamp 추가
  const updateDownloadStatus = useCallback((themeId, status) => {
    setDownloadStatus(prev => {
      // 새로운 상태 구조 사용
      let newStatus;
      
      if (status === 'downloading') {
        // downloading 상태는 timestamp와 함께 저장
        newStatus = {
          status: 'downloading',
          timestamp: Date.now()
        };
      } else if (status === 'downloaded' || status === 'failed' || status === 'not-downloaded') {
        // 다른 상태는 단순 문자열로 저장 (하위 호환성)
        newStatus = status;
      } else {
        newStatus = status;
      }
      
      // 이미 같은 상태면 업데이트하지 않음 (무한 루프 방지)
      const currentStatus = prev[themeId];
      const isSameStatus = (
        (typeof currentStatus === 'string' && currentStatus === status) ||
        (typeof currentStatus === 'object' && currentStatus?.status === status)
      );
      
      if (isSameStatus) {
        return prev;
      }
      
      const updatedStatus = {
        ...prev,
        [themeId]: newStatus
      };
      
      // localStorage 즉시 업데이트
      localStorage.setItem(DOWNLOAD_STATUS_KEY, JSON.stringify(updatedStatus));
      return updatedStatus;
    });
  }, [DOWNLOAD_STATUS_KEY]);
  
  const updateDownloadProgress = useCallback((themeId, progress) => {
    setDownloadProgress(prev => {
      // 이미 같은 진행률이면 업데이트하지 않음 (무한 루프 방지)
      if (prev[themeId] === progress) {
        return prev;
      }
      return {
        ...prev,
        [themeId]: progress
      };
    });
  }, []);
  
  // 테마 다운로드 상태 확인 함수 - 새로운 구조 처리
  const isThemeDownloaded = useCallback((themeId) => {
    const status = downloadStatus[themeId];
    
    // 하위 호환성: 문자열과 객체 형식 모두 처리
    if (typeof status === 'string') {
      return status === 'downloaded';
    } else if (status && typeof status === 'object') {
      return status.status === 'downloaded';
    }
    
    return false;
  }, [downloadStatus]);
  
  // 컴포넌트 마운트 시 상태 재검증
  useEffect(() => {
    const revalidateStatus = () => {
      const saved = localStorage.getItem(DOWNLOAD_STATUS_KEY);
      if (saved) {
        try {
          const parsedStatus = JSON.parse(saved);
          const validatedStatus = validateDownloadStatus(parsedStatus);
          
          // 검증 결과가 다르면 업데이트
          if (JSON.stringify(parsedStatus) !== JSON.stringify(validatedStatus)) {
            debugLog('[ThemeDownloadContext] 상태 재검증 - 변경사항 발견');
            setDownloadStatus(validatedStatus);
            localStorage.setItem(DOWNLOAD_STATUS_KEY, JSON.stringify(validatedStatus));
          }
        } catch (error) {
          debugLog('[ThemeDownloadContext] 재검증 실패:', error);
        }
      }
    };
    
    // 초기 마운트 시 한 번 실행
    revalidateStatus();
    
    // 포커스 이벤트 시 재검증 (탭 전환 시)
    const handleFocus = () => {
      debugLog('[ThemeDownloadContext] 윈도우 포커스 - 상태 재검증');
      revalidateStatus();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [DOWNLOAD_STATUS_KEY]);
  
  const value = {
    // 상태
    downloadStatus,
    downloadProgress,
    
    // 함수
    updateDownloadStatus,
    updateDownloadProgress,
    isThemeDownloaded,
    
    // 키 노출 (useThemeDownload에서 사용)
    DOWNLOAD_STATUS_KEY
  };
  
  return (
    <ThemeDownloadContext.Provider value={value}>
      {children}
    </ThemeDownloadContext.Provider>
  );
};